import React, { useMemo, useState } from 'react';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import { NavLink } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import MainContent from '../components/MainContent';
import Modal from '../components/Modal';
import Spinner from '../components/spinner/Spinner';
import TableWrapper from '../utils/TableWrapper';
import useFetchDatav2 from '../hooks/useFetchDatav2';
import usePermissions from '../hooks/usePermissions';
import useGetUserRole from '../hooks/useGetUserRole';
import { axiosInstancev2 } from '../utils/Axios/axiosInterceptorv2';

const RESERVED_CODES = [
  'PLP',
  'LMS',
  'ABC',
  'XYZ',
  'XXX',
  'TST',
  'NIL',
  'NUL',
  'ERR',
  'NEW',
  'TBD',
];

const COHORT_TYPES = ['PARTNER', 'SCHOLARSHIP', 'ACCELERATOR', 'SYSTEM'];

function codeMessageColor(code, isOk) {
  if (!code) return 'text-gray-500';
  return isOk ? 'text-green-600' : 'text-red-600';
}

export function suggestPrefixCode(
  name,
  { reserved = RESERVED_CODES, taken = [] } = {}
) {
  if (!name) return '';
  const clean = name
    .toUpperCase()
    .replace(/[^A-Z\s]/g, '')
    .trim();
  if (!clean) return '';

  const words = clean.split(/\s+/).filter(Boolean);
  const blocked = new Set([...reserved, ...taken].map((s) => s.toUpperCase()));

  const candidates = [];
  if (words.length >= 3) {
    candidates.push(
      words
        .slice(0, 3)
        .map((w) => w[0])
        .join('')
    );
  }
  if (words[0].length >= 3) {
    candidates.push(words[0].slice(0, 3));
  }
  if (words.length >= 2 && words[1].length >= 2) {
    candidates.push(words[0][0] + words[1].slice(0, 2));
  }
  const consonants = words[0].replace(/[AEIOU]/g, '');
  if (consonants.length >= 3) {
    candidates.push(consonants.slice(0, 3));
  }

  const winner = candidates.find((c) => c.length === 3 && !blocked.has(c));
  return winner || '';
}

const columns = [
  {
    title: 'Code',
    field: 'code',
    render: (row) => (
      <p className="bg-blue-100 text-blue-800 text-center py-1 rounded-md font-mono text-xs">
        {row.code}
      </p>
    ),
  },
  { title: 'Name', field: 'name' },
  {
    title: 'Type',
    field: 'cohort_type',
    render: (row) => (
      <p className="bg-gray-100 text-gray-700 text-center py-1 rounded-md text-xs uppercase">
        {row.cohort_type}
      </p>
    ),
  },
  { title: 'Description', field: 'description' },
  {
    title: 'Status',
    field: 'is_active',
    render: (row) => (
      <p
        className={`text-center py-1 rounded-md font-medium ${
          row.is_active
            ? 'bg-green-100 text-green-700'
            : 'bg-red-100 text-red-700'
        }`}
      >
        {row.is_active ? 'Active' : 'Inactive'}
      </p>
    ),
  },
];

function CohortPrefixForm({ existingPrefixes, toggleModal }) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [codeTouched, setCodeTouched] = useState(false);
  const [cohortType, setCohortType] = useState('PARTNER');
  const [description, setDescription] = useState('');
  const queryClient = useQueryClient();

  const takenCodes = useMemo(
    () => existingPrefixes.map((p) => p.code),
    [existingPrefixes]
  );

  const handleNameChange = (e) => {
    const next = e.target.value;
    setName(next);
    if (!codeTouched) {
      setCode(suggestPrefixCode(next, { taken: takenCodes }));
    }
  };

  const handleCodeChange = (e) => {
    setCodeTouched(true);
    setCode(
      e.target.value
        .toUpperCase()
        .replace(/[^A-Z]/g, '')
        .slice(0, 3)
    );
  };

  const validation = useMemo(() => {
    if (!code) return { ok: false, msg: '' };
    if (code.length !== 3)
      return { ok: false, msg: 'Code must be exactly 3 letters' };
    if (RESERVED_CODES.includes(code))
      return { ok: false, msg: `${code} is reserved` };
    if (takenCodes.includes(code))
      return { ok: false, msg: `${code} already exists` };
    return { ok: true, msg: 'Available' };
  }, [code, takenCodes]);

  const mutation = useMutation(
    (payload) => axiosInstancev2.post('/cohorts/prefixes', payload),
    {
      onSuccess: () => {
        toast.success('Prefix created');
        queryClient.invalidateQueries(['cohort-prefixes']);
        toggleModal();
      },
      onError: (error) => {
        toast.error(error?.response?.data?.error || 'Failed to create prefix');
      },
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Partner / cohort name is required');
      return;
    }
    if (!validation.ok) {
      toast.error(validation.msg || 'Fix the prefix code first');
      return;
    }
    mutation.mutate({
      code,
      name: name.trim(),
      cohort_type: cohortType,
      description: description.trim(),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-blue-800 text-sm">
          Enter the partner / programme name. We&apos;ll suggest a 3-letter
          prefix you can edit. Final student IDs use the format{' '}
          <span className="font-mono">PLP-{code || 'XXX'}-YYMM-SSSSS</span>.
        </p>
      </div>

      <div className="flex flex-col space-y-1">
        <label htmlFor="prefix-name" className="text-base font-medium">
          Partner / Cohort Name
        </label>
        <input
          id="prefix-name"
          type="text"
          value={name}
          onChange={handleNameChange}
          placeholder="e.g. Dariu Foundation"
          className="border border-gray-300 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-claret-500"
        />
      </div>

      <div className="flex flex-col space-y-1">
        <label htmlFor="prefix-code" className="text-base font-medium">
          Prefix Code
        </label>
        <input
          id="prefix-code"
          type="text"
          value={code}
          onChange={handleCodeChange}
          placeholder="DAR"
          maxLength={3}
          className="border border-gray-300 px-3 py-2 rounded-lg font-mono uppercase tracking-widest focus:outline-none focus:ring-2 focus:ring-claret-500"
        />
        <p className={`text-xs mt-1 ${codeMessageColor(code, validation.ok)}`}>
          {code
            ? validation.msg
            : 'Auto-suggested from the name; tweak if you prefer a different code.'}
        </p>
      </div>

      <div className="flex flex-col space-y-1">
        <label htmlFor="cohort-type" className="text-base font-medium">
          Cohort Type
        </label>
        <select
          id="cohort-type"
          value={cohortType}
          onChange={(e) => setCohortType(e.target.value)}
          className="border border-gray-300 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-claret-500"
        >
          {COHORT_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-col space-y-1">
        <label htmlFor="prefix-description" className="text-base font-medium">
          Description
        </label>
        <textarea
          id="prefix-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="e.g. Dariu Foundation sponsored"
          rows={2}
          className="border border-gray-300 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-claret-500"
        />
      </div>

      {mutation.isLoading ? (
        <div className="flex justify-center py-4">
          <Spinner />
        </div>
      ) : (
        <div className="flex justify-between mt-6">
          <button
            type="button"
            onClick={toggleModal}
            className="border border-claret-500 px-4 py-2 rounded-md text-claret-500 text-sm flex items-center space-x-2"
          >
            <CancelIcon fontSize="inherit" />
            <span>Cancel</span>
          </button>
          <button
            type="submit"
            disabled={!validation.ok || !name.trim()}
            className="bg-claret-500 px-4 py-2 rounded-md text-white text-sm flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <SaveIcon fontSize="inherit" />
            <span>Save Prefix</span>
          </button>
        </div>
      )}
    </form>
  );
}

function CohortPrefixes() {
  const [modalOpen, setModalOpen] = useState(false);
  const permissions = usePermissions();
  const role = useGetUserRole();
  const canCreate =
    permissions?.includes('UpdateCohort') || role === 'SuperAdmin';

  const toggleModal = () => setModalOpen((v) => !v);

  const { data, isLoading } = useFetchDatav2(
    ['cohort-prefixes'],
    '/cohorts/prefixes',
    { params: { active_only: 'false' } },
    "Couldn't load Student ID prefixes",
    true,
    true
  );

  const prefixes = data?.data?.prefixes || [];

  return (
    <>
      <MainContent full>
        <div className="bg-white px-2 py-5 rounded-lg flex justify-between">
          <Breadcrumbs aria-label="breadcrumb" separator={<NavigateNextIcon />}>
            <NavLink
              underline="hover"
              color="inherit"
              to="/"
              className="flex items-center"
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              <p>Home</p>
            </NavLink>
            <NavLink underline="hover" color="inherit" to="/cohorts">
              <p>Cohorts</p>
            </NavLink>
            <p className="text-gray-600">Student ID Prefixes</p>
          </Breadcrumbs>
          {canCreate && (
            <button
              type="button"
              onClick={toggleModal}
              className="bg-claret-500 text-white text-sm py-1 px-5 rounded-md cursor-pointer flex items-center space-x-2"
            >
              <AddIcon fontSize="inherit" />
              <span>Add Partner Prefix</span>
            </button>
          )}
        </div>
        <div className="mt-4">
          {isLoading ? (
            <Spinner />
          ) : (
            <TableWrapper
              title="Student ID Prefix Catalog"
              columns={columns}
              data={prefixes}
            />
          )}
        </div>
      </MainContent>
      <Modal
        modalOpen={modalOpen}
        toggleModal={toggleModal}
        title="Add Student ID Prefix"
      >
        <CohortPrefixForm
          existingPrefixes={prefixes}
          toggleModal={toggleModal}
        />
      </Modal>
    </>
  );
}

export default CohortPrefixes;
